import createSagaMiddleware from '@redux-saga/core'
import { configureStore, combineReducers, Middleware } from '@reduxjs/toolkit'
import logger from 'redux-logger'
import { applicationReducer } from 'redux/application/applicationSlice'
import { conciergeReducer } from 'redux/concierge/conciergeSlice'
import rootSaga from 'redux/config/saga/sagas'
import { dashboardReducer } from 'features/Dashboard/dashboardSlice'
import { employeeReducer } from 'redux/employee/employeeSlice'
import { employerReducer } from 'redux/employer/employerSlice'
import { flexReducer } from 'redux/flex/flexSlice'
import { transitPassReducer } from 'redux/transitPass/transitPassSlice'
import { qtfReducer } from 'redux/qtf/qtfSlice'
import { incentivesReducer } from 'redux/incentives/incentivesSlice'
import { leaderboardsReducer } from 'redux/leaderboards/leaderboardsSlice'
import { mapIntelligenceReducer } from 'redux/mapIntelligence/mapIntelligenceSlice'
import { micromobilityReducer } from 'redux/micromobility/micromobilitySlice'
import { notificationsReducer } from 'redux/notifications/notificationsSlice'
import { programsReducer } from 'redux/programs/programsSlice'
import { userReducer } from 'redux/user/userSlice'
import Analytics from 'shared/Analytics'
import { grhReducer } from 'redux/grh/grhSlice'
import { otpReducer } from 'redux/otp/otpSlice'
import { employeePlatformReducer } from 'redux/employeePlatform/employeePlatformSlice'
import { optInReducer } from 'redux/optin/optInSlice'
import { finchProviders } from 'features/Finch/finchSlice'
import { conciergeAIReducer } from 'features/Concierge-AI/conciergeAISlice'
import { launchProgramsReducer } from 'features/Programs/Launch/launchProgramsSlice'
import { programSuccessReducer } from 'features/Programs/Success/ProgramSuccessSlice'
import { programEditingReducer } from 'features/Programs/Editing/ProgramEditingSlice'
import { onboardingReducer } from 'features/Onboarding/onboardingSlice'
import { routerReducer } from 'shared/Router/RouterSlice'
import { ssoReducer } from 'features/SSO/SSOSlice'
import { reimbursementsReducer } from 'redux/reimbursements/reimbursementsSlice'
import { tagsReducer } from 'redux/tags/tagsSlice'
import { bikeCatalogReducer } from 'redux/bikeCatalog/bikeCatalogSlice'
import { autoEnrollReducer } from 'features/AutoEnroll'
import { autoInviteReducer } from 'features/AutoInvite'
import { apiRosterSyncReducer } from 'features/ApiRosterSync'
import { autoTopupReducer } from 'redux/auto-topup/autoTopupSlice'
import { automatedReportingReducer } from 'redux/automatedReporting/automatedReportingSlice'
import { reportsReducer } from 'features/Report/ReportsSlice'
import { complianceReducer } from 'redux/compliance/complianceSlice'

const SagaMiddleware = createSagaMiddleware()
const analyticsMiddleware = Analytics as unknown as Middleware

// Create the root reducer
const appReducer = combineReducers({
  user: userReducer,
  application: applicationReducer,
  employer: employerReducer,
  employee: employeeReducer,
  mapIntelligence: mapIntelligenceReducer,
  programs: programsReducer,
  notifications: notificationsReducer,
  concierge: conciergeReducer,
  dashboard: dashboardReducer,
  incentives: incentivesReducer,
  leaderboards: leaderboardsReducer,
  micromobility: micromobilityReducer,
  flex: flexReducer,
  transitPass: transitPassReducer,
  qtf: qtfReducer,
  grh: grhReducer,
  optIn: optInReducer,
  otp: otpReducer,
  employeePlatform: employeePlatformReducer,
  finchProviders,
  conciergeAI: conciergeAIReducer,
  launchPrograms: launchProgramsReducer,
  programSuccess: programSuccessReducer,
  programEditing: programEditingReducer,
  reports: reportsReducer,
  compliance: complianceReducer,
  automatedReporting: automatedReportingReducer,
  onboarding: onboardingReducer,
  router: routerReducer,
  sso: ssoReducer,
  reimbursements: reimbursementsReducer,
  tags: tagsReducer,
  bikeCatalog: bikeCatalogReducer,
  autoEnroll: autoEnrollReducer,
  autoInvite: autoInviteReducer,
  apiRosterSync: apiRosterSyncReducer,
  autoTopup: autoTopupReducer,
})

/*
 * Create a root reducer that can reset the entire state
 * This is coming from the userSaga.ts logout function
 */
const rootReducer = (state: any, action: any) => {
  if (action.type === 'RESET_STORE') {
    // Preserve SSO state since it's not user-specific and needed on login page
    const preservedSsoState = state?.sso
    const resetState = appReducer(undefined, action)
    return {
      ...resetState,
      sso: preservedSsoState || resetState.sso,
    }
  }
  return appReducer(state, action)
}

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    //add redux logger if on develop
    if (process.env.NODE_ENV === 'development') {
      return getDefaultMiddleware()
        .prepend(SagaMiddleware)
        .concat(logger, analyticsMiddleware)
    }

    return getDefaultMiddleware()
      .prepend(SagaMiddleware)
      .concat(analyticsMiddleware)
  },
})

export type AppState = ReturnType<typeof store.getState>

SagaMiddleware.run(rootSaga)

export default store
